import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  EmailAuthProvider,
  onIdTokenChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  updatePassword,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  describeFirebaseAuthError,
  firebaseAuth,
  firebaseStorage,
  getAppCheckToken,
} from '@/lib/firebase';
import { registerTokenProvider, request } from '@/lib/apiClient';
import { isApiError } from '@/lib/apiError';
import { useToast } from '@/components/ui/toaster';
import { endpoints } from '@/features/endpoints';
import { isStaffRole, Role, type AuthMe, type StaffChallenge } from '@/types/domain';

// -----------------------------------------------------------------------------
// Session.
//
// Sign-in is TWO SERVER ROUND TRIPS AND THEN A FOUR-STEP GATE.
//
// The two round trips are the second factor, and the browser holds no
// credential across them:
//
//   A. `startSignIn(identifier, password)` posts to /auth/staff/challenge. The
//      SERVER checks the password against Identity Toolkit and emails a code.
//      Nothing comes back but a challenge id - no token, not even the account's
//      email. The panel is still signed out.
//   B. `completeSignIn(challengeId, code)` posts to /auth/staff/verify and gets
//      a CUSTOM TOKEN, which is the only credential this app ever signs in
//      with. It carries an `mfa` claim that the API demands on every /admin/*
//      request.
//
// The browser deliberately never calls `signInWithEmailAndPassword`. Doing so
// would create a fully valid session BEFORE the code was entered, and an OTP
// screen in front of an already-open door is theatre. See the header of
// `Backend/src/modules/auth/staffAuth.service.ts`.
//
// Then the gate that was always here, unchanged:
//
//   1. A Firebase credential exists. Proves identity.
//   2. Read `role` from the CUSTOM CLAIM on the decoded token. Not from a
//      request body, not from a Firestore field the client could write - the
//      claim is what Firestore rules and the API both enforce on.
//   3. Only if the claim says admin/super_admin: POST /auth/session, then
//      GET /auth/me for the server's authoritative view.
//   4. If /auth/me disagrees, sign out.
//
// Step 2 gating step 3 is not decoration. `POST /auth/session` CREATES an
// account when none exists, and its schema only accepts `user | trainer` - so
// calling it for a non-staff identity would provision that person as a MEMBER of
// the platform. See `Backend/src/modules/auth/auth.service.ts:189`.
//
// None of this is a security boundary. The API enforces authorization on every
// request; this exists so staff get a clear "you don't have access" instead of a
// shell full of 403s.
// -----------------------------------------------------------------------------

export type SessionUser = {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: 'admin' | 'super_admin';
};

type AuthStatus = 'initialising' | 'authenticated' | 'unauthenticated';

type AuthContextValue = {
  status: AuthStatus;
  user: SessionUser | null;
  /** Set when a sign-in was rejected - wrong password, or not a staff account. */
  error: string | null;
  /**
   * True for the WHOLE sign-in chain, not just the credential call.
   *
   * Sign-in is four steps: Firebase credential → read the role claim → POST
   * /auth/session → GET /auth/me. The credential resolves first and the other
   * three are driven by the token listener, so a button bound to the submit
   * promise alone goes idle in the middle and the screen flickers back to "Sign
   * in" a moment before it redirects. That was a real, reported glitch. This
   * flag spans the entire chain so the button never lies about being finished.
   */
  isVerifying: boolean;
  /**
   * Step A. Verifies the password server-side and sends a one-time code.
   *
   * `identifier` is an email OR a mobile number - the server resolves which.
   * Resolving to a challenge does NOT sign anyone in.
   */
  startSignIn(identifier: string, password: string): Promise<StaffChallenge>;
  /** Step B. Exchanges the code for a session. */
  completeSignIn(challengeId: string, code: string): Promise<void>;
  /** Issues a NEW code against the same challenge. Rate-limited server-side. */
  resendCode(challengeId: string): Promise<StaffChallenge>;
  signOut(): Promise<void>;
  clearError(): void;
  /**
   * Sends Firebase's password-reset email.
   *
   * Resolves even when the address has no account - see the note at the call
   * site. Never reveals whether it did.
   */
  sendPasswordReset(email: string): Promise<void>;
  /** Re-authenticates with the current password, then sets a new one. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  /** Uploads an avatar and points the Firebase profile at it. */
  updateAvatar(file: File): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const NOT_STAFF_MESSAGE = 'This account does not have admin access.';

/**
 * `POST /auth/session` requires a role and its schema rejects `admin`.
 *
 * The value below is a formality, not an assertion: `auth.service.ts:146`
 * explicitly exempts ADMIN and SUPER_ADMIN accounts from the role-mismatch check
 * for exactly this case, and the response's role comes from the stored document
 * either way. It is only ever sent for an identity whose claim has ALREADY been
 * verified as staff, so it cannot create a member account.
 *
 * FLAGGED: the clean fix is `sessionSchema` accepting the admin roles, or a
 * dedicated `POST /admin/auth/session`. Until then this comment is the contract.
 */
const SESSION_ROLE_PLACEHOLDER = Role.USER;

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('initialising');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // `ToasterProvider` sits ABOVE this one in `providers.tsx`, so the welcome
  // toast survives the redirect from /sign-in into the console - a toast raised
  // inside the login screen would unmount with it.
  const toast = useToast();

  // The live Firebase user, held in a ref so the token provider registered below
  // reads the current one without the effect re-running on every change.
  const firebaseUserRef = useRef<FirebaseUser | null>(null);

  // Guards against a stale in-flight verification overwriting a newer one - a
  // fast sign-out/sign-in pair would otherwise resolve out of order.
  const verificationIdRef = useRef(0);

  /**
   * Set when the user actually submitted the sign-in form.
   *
   * Distinguishes a deliberate sign-in from a session restored on page load or a
   * silent token refresh, both of which also fire `onIdTokenChanged`. A ref
   * rather than state: reading it inside the listener must not re-subscribe the
   * listener, and its value is consumed exactly once.
   */
  const signInAttemptedRef = useRef(false);

  const signOut = useCallback(async (): Promise<void> => {
    verificationIdRef.current += 1;
    await firebaseSignOut(firebaseAuth);
    firebaseUserRef.current = null;
    setUser(null);
    setStatus('unauthenticated');
    setIsVerifying(false);
  }, []);

  // -- Token plumbing ---------------------------------------------------------
  //
  // Registered once, and BEFORE the token listener below - effects run in
  // declaration order, so the first request the listener makes already carries
  // both headers.
  //
  // The client asks for both tokens per request; each SDK returns its cached one
  // until it is close to expiry, so neither is a network call on every request.
  //
  // App Check is registered here despite having nothing to do with the session,
  // because the client's token plumbing has exactly one owner and a second
  // registration would silently replace this one.
  useEffect(() => {
    registerTokenProvider({
      async getToken(force = false) {
        const current = firebaseUserRef.current;
        if (!current) return null;
        try {
          return await current.getIdToken(force);
        } catch {
          // A refresh that fails means the refresh token is gone or revoked.
          return null;
        }
      },
      // Owned by `firebase.ts`, which is where App Check is initialised and so
      // is the only place that can report a failure with the context needed to
      // act on it. Passed through unwrapped.
      getAppCheckToken,
      onAuthFailure() {
        void signOut();
      },
    });
  }, [signOut]);

  // -- The gate ---------------------------------------------------------------
  useEffect(() => {
    // `onIdTokenChanged` rather than `onAuthStateChanged`: it also fires on every
    // silent hourly token refresh, which is what keeps `firebaseUserRef` holding
    // a user whose `getIdToken()` returns a live token rather than a stale one.
    const unsubscribe = onIdTokenChanged(firebaseAuth, async (firebaseUser) => {
      firebaseUserRef.current = firebaseUser;

      if (!firebaseUser) {
        setUser(null);
        setStatus('unauthenticated');
        setIsVerifying(false);
        return;
      }

      const verificationId = ++verificationIdRef.current;
      const isStale = (): boolean => verificationId !== verificationIdRef.current;

      // A token has arrived and the remaining three steps are about to run.
      // Holding the flag here - not only in `signIn` - is what keeps the login
      // button busy across the whole chain instead of going idle the moment the
      // credential resolves.
      setIsVerifying(true);

      try {
        // Step 2 - the claim.
        const tokenResult = await firebaseUser.getIdTokenResult();
        const claimRole = tokenResult.claims.role;

        if (!isStaffRole(typeof claimRole === 'string' ? claimRole : null)) {
          if (isStale()) return;
          setError(NOT_STAFF_MESSAGE);
          await signOut();
          return;
        }

        // Step 3 - tell the server we are here, then ask it who we are.
        await request<unknown>('/auth/session', {
          method: 'POST',
          body: { role: SESSION_ROLE_PLACEHOLDER },
        });

        const me = await request<AuthMe>('/auth/me');
        if (isStale()) return;

        // Step 4 - the server is the authority. A claim that has not caught up
        // with a revoked role would otherwise let a demoted admin back in for
        // up to an hour.
        if (!isStaffRole(me.role)) {
          setError(NOT_STAFF_MESSAGE);
          await signOut();
          return;
        }

        setUser({
          uid: me.uid,
          email: me.email ?? firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          role: me.role,
        });
        setError(null);
        setStatus('authenticated');
        setIsVerifying(false);

        // Announce only a REAL sign-in.
        //
        // `onIdTokenChanged` also fires on a page reload with a stored session,
        // and on every silent hourly token refresh. Toasting on all of them
        // would greet the user again on every F5, and once an hour while they
        // work. The ref is set by `signIn` and consumed here, so exactly one
        // toast follows exactly one deliberate sign-in.
        if (signInAttemptedRef.current) {
          signInAttemptedRef.current = false;
          const firstName = firebaseUser.displayName?.trim().split(/\s+/)[0];
          toast.success(
            firstName ? `Welcome back, ${firstName}` : 'Signed in',
            me.role === Role.SUPER_ADMIN
              ? 'Super admin - you have full access, including roles and refunds.'
              : 'Signed in as an administrator.',
          );
        }
      } catch (verificationError) {
        if (isStale()) return;

        setError(
          isApiError(verificationError) && verificationError.message
            ? verificationError.message
            : 'Could not verify your session. Try again.',
        );
        await signOut();
      }
    });

    return unsubscribe;
    // `toast` is stable - `ToasterProvider` memoises its context value - so
    // including it does not re-subscribe the listener on every render.
  }, [signOut, toast]);

  /**
   * Step A - password.
   *
   * Nothing is signed in here and nothing is stored. A failure is re-thrown for
   * the form to render inline; it is deliberately NOT put into `error`, which
   * drives the shell-level banner and would outlive the login screen.
   */
  const startSignIn = useCallback(
    async (identifier: string, password: string): Promise<StaffChallenge> => {
      setError(null);
      return request<StaffChallenge>(endpoints.auth.staff.challenge, {
        method: 'POST',
        body: { identifier: identifier.trim(), password },
      });
    },
    [],
  );

  const resendCode = useCallback(
    (challengeId: string): Promise<StaffChallenge> =>
      request<StaffChallenge>(endpoints.auth.staff.resend, {
        method: 'POST',
        body: { challengeId },
      }),
    [],
  );

  /**
   * Step B - the code.
   *
   * The custom token is exchanged for a Firebase session, which fires the token
   * listener above and runs the four-step gate. The token itself is never
   * stored: `signInWithCustomToken` consumes it, and Firebase holds the refresh
   * token from there.
   */
  const completeSignIn = useCallback(
    async (challengeId: string, code: string): Promise<void> => {
      setError(null);

      // Thrown before any local state changes, so a wrong code leaves the form
      // exactly as it was with a message beside it.
      const { customToken } = await request<{ customToken: string }>(
        endpoints.auth.staff.verify,
        { method: 'POST', body: { challengeId, code } },
      );

      // Read back by the token listener to tell a deliberate sign-in apart from
      // a restored session. Set BEFORE the credential call, because the listener
      // can fire before `signInWithCustomToken` resolves.
      signInAttemptedRef.current = true;
      setIsVerifying(true);

      try {
        await signInWithCustomToken(firebaseAuth, customToken);
      } catch (signInError) {
        const firebaseCode =
          typeof signInError === 'object' && signInError !== null && 'code' in signInError
            ? String((signInError as { code: unknown }).code)
            : '';
        const message = describeFirebaseAuthError(firebaseCode);
        signInAttemptedRef.current = false;
        setIsVerifying(false);
        // The safe copy is what the user sees; the original Firebase error is
        // kept on `cause` so a devtools stack still shows which code it was.
        throw new Error(message, { cause: signInError });
      }
    },
    [],
  );

  /**
   * Sends Firebase's own password-reset email.
   *
   * Firebase delivers this from its infrastructure using the template in
   * Console → Authentication → Templates. No SMTP server, no mail provider and
   * no backend involvement - which is why this lives on the client rather than
   * behind an endpoint: the Admin SDK can only GENERATE a reset link, it cannot
   * send one.
   *
   * `auth/user-not-found` is swallowed on purpose. Reporting it would turn this
   * form into an account-enumeration oracle: anyone could discover which
   * addresses hold staff accounts by watching which ones error. The caller shows
   * the same "if an account exists, we sent it" message either way. Genuine
   * failures - offline, rate-limited, bad project config - still throw, because
   * those the operator has to know about.
   */
  const sendPasswordReset = useCallback(async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(firebaseAuth, email.trim());
    } catch (resetError) {
      const code =
        typeof resetError === 'object' && resetError !== null && 'code' in resetError
          ? String((resetError as { code: unknown }).code)
          : '';

      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') return;

      throw new Error(describeFirebaseAuthError(code), { cause: resetError });
    }
  }, []);

  /**
   * Changes the signed-in staff member's password.
   *
   * Re-authenticates first, always. Firebase requires a recent login for
   * `updatePassword`, but the more important reason is that a session left open
   * on an unlocked machine must not be enough to take the account over - asking
   * for the current password is what makes this a change rather than a hijack.
   */
  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<void> => {
      const current = firebaseUserRef.current;
      if (!current?.email) throw new Error('You are not signed in.');

      try {
        await reauthenticateWithCredential(
          current,
          EmailAuthProvider.credential(current.email, currentPassword),
        );
      } catch (reauthError) {
        const code =
          typeof reauthError === 'object' && reauthError !== null && 'code' in reauthError
            ? String((reauthError as { code: unknown }).code)
            : '';
        // Report this as a wrong CURRENT password rather than as a failed
        // sign-in, which is what Firebase's generic copy would imply here.
        throw new Error(
          code === 'auth/invalid-credential' || code === 'auth/wrong-password'
            ? 'That is not your current password.'
            : describeFirebaseAuthError(code),
          { cause: reauthError },
        );
      }

      try {
        await updatePassword(current, newPassword);
      } catch (updateError) {
        const code =
          typeof updateError === 'object' && updateError !== null && 'code' in updateError
            ? String((updateError as { code: unknown }).code)
            : '';
        throw new Error(describeFirebaseAuthError(code), { cause: updateError });
      }
    },
    [],
  );

  /**
   * Uploads an avatar and points the Firebase profile at it.
   *
   * `users/{uid}/profile/…` is a path `Backend/storage.rules` already permits
   * for the owning user, so this needs no rules change - and the rules, not this
   * function, are what actually enforce ownership, type and size.
   *
   * The local user object is updated by hand afterwards. `updateProfile` does
   * NOT fire `onIdTokenChanged`, so without this the new photo would not appear
   * until the next reload.
   */
  const updateAvatar = useCallback(async (file: File): Promise<void> => {
    const current = firebaseUserRef.current;
    if (!current) throw new Error('You are not signed in.');

    // Mirrors the rules' own limits so the failure is a readable message rather
    // than an opaque storage/unauthorized.
    if (!/^image\/(jpeg|png|webp|heic)$/.test(file.type)) {
      throw new Error('Use a JPEG, PNG, WebP or HEIC image.');
    }
    if (file.size >= 8 * 1024 * 1024) {
      throw new Error('That image is over 8 MB. Use a smaller one.');
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
    const fileRef = storageRef(firebaseStorage, `users/${current.uid}/profile/avatar.${extension}`);

    await uploadBytes(fileRef, file, { contentType: file.type });
    const photoURL = await getDownloadURL(fileRef);

    await updateProfile(current, { photoURL });
    setUser((previous) => (previous ? { ...previous, photoURL } : previous));
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      error,
      isVerifying,
      startSignIn,
      completeSignIn,
      resendCode,
      signOut,
      clearError,
      sendPasswordReset,
      changePassword,
      updateAvatar,
    }),
    [
      status,
      user,
      error,
      isVerifying,
      startSignIn,
      completeSignIn,
      resendCode,
      signOut,
      clearError,
      sendPasswordReset,
      changePassword,
      updateAvatar,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}

/**
 * The signed-in staff member. Throws when called outside an authenticated tree.
 *
 * Screens sit behind `<RequireAuth>`, so a null user there is a routing bug, not
 * a state a component should have to branch on.
 */
export function useSessionUser(): SessionUser {
  const { user } = useAuth();
  if (!user) throw new Error('useSessionUser called outside an authenticated route.');
  return user;
}
